import React, {
	type ComponentType,
	memo,
	useCallback,
	useLayoutEffect,
	useMemo,
	useState,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type PropsWithChildren,
	Fragment,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { Placement as IssueNavigatorPlacement } from '@post-office/placements/issue-navigator-placement';
import DeleteIssueView from '@jira/issue__issue-actions-menu/src/delete-issue/delete-issue-view.tsx';
import { token } from '@atlaskit/tokens';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { useDeleteIssueModalStates } from '@atlassian/jira-issue-navigator-issue-operations/src/ui/dropdown-menu-group/issue-delete/controllers/selectors.tsx';
import { HideDoneOnboardingProvider } from '@atlassian/jira-issue-table-hide-done-toggle/src/ui/onboarding/index.tsx';
import { IssueFieldStatisticsEntrypointProvider } from '@atlassian/jira-issue-navigator-action-issue-field-statistics/src/controllers/entrypoint-context/index.tsx';
import { AnnouncerV2 } from '@atlassian/jira-accessibility/src/ui/announcer-v2/index.tsx';
import { ssr } from '@atlassian/jira-browser-metrics/src/bm3-init/ssr/index.tsx';
import { asyncBulkOpsCoreEntryPoint } from '@atlassian/jira-bulk-operations-async-core/entrypoint.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { useGlobalScopeOptInFlag } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/enable-nin-changeboarding/index.tsx';
import { IssueTableSelectedIssuesContainer } from '@atlassian/jira-native-issue-table/src/controllers/selected-issues/index.tsx';
import { HierarchyOnboardingProvider } from '@atlassian/jira-issue-table-hierarchy/src/ui/hierarchy-onboarding/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { jqlBuilder_issueNavigator_JQLBuilderWrapper_filter$key as JqlBuilderFilterFragmentType } from '@atlassian/jira-relay/src/__generated__/jqlBuilder_issueNavigator_JQLBuilderWrapper_filter.graphql';
import type { main_issueNavigator_Header_filter$key as HeaderFilterFragmentType } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_Header_filter.graphql';
import type { main_issueNavigator_IssueNavigatorUI_groupResults$key as GroupResultsFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigatorUI_groupResults.graphql';
import type { main_issueNavigator_IssueNavigatorUI_issueResults$key as IssueResultsFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigatorUI_issueResults.graphql';
import type { main_issueNavigator_IssueNavigatorUI_jiraView$key as JiraViewFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigatorUI_jiraView.graphql';
import type { main_issueNavigator_IssueNavigatorUI_jqlBuilderWithAiKey$key as JqlBuilderWithAiKey } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigatorUI_jqlBuilderWithAiKey.graphql';
import type { main_issueNavigator_IssueNavigatorUI_project$key as ProjectFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigatorUI_project.graphql';
import type { main_issueNavigator_IssueNavigatorUI_userPreferences$key as UserPreferencesFragmentKey } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigatorUI_userPreferences.graphql';
import type { main_issueNavigator_ListView_filter$key as ListViewFilterFragmentType } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_ListView_filter.graphql';
import type { topBar_issueNavigator_filter$key as TopBarFilterFragment } from '@atlassian/jira-relay/src/__generated__/topBar_issueNavigator_filter.graphql';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { SidebarLayout as BaseSidebarLayout } from '@atlassian/jira-platform-sidebar/src/ui/index.tsx';
import type { main_issueNavigator_SidebarLayout_project$key } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_SidebarLayout_project.graphql';
import { useDeleteIssueModalActions } from '@atlassian/jira-issue-navigator-issue-operations/src/ui/dropdown-menu-group/issue-delete/controllers/main.tsx';
import { JQLPanelRenderSlotForWorkItemList } from '@atlassian/jira-jql-debugger/src/ui/debugger-panel/index.tsx';
import {
	PACKAGE_NAME,
	isJiraIssueSearchViewMetadata,
	GROUPED_LIST_EXPERIENCE_ID,
} from '../common/constants.tsx';
import type {
	CustomHeaderProps,
	OverridableIssueTableProps,
	IssueNavigatorViewId,
} from '../common/types.tsx';
import { NoFilter } from '../common/ui/no-filter/index.tsx';
import { markOnce, marks } from '../common/utils/performance-analytics.tsx';
import {
	IssueSearchFail,
	IssueSearchSuccess,
} from '../controllers/issue-search-analytics/index.tsx';
import { useIsFullPageIssueAppMode } from '../controllers/selected-issue/hooks.tsx';
import { useSelectedViewState } from '../controllers/selected-view-state/index.tsx';
import { useActiveJql, useResolvedJql } from '../services/active-jql/index.tsx';
import { useFilterQuery } from '../services/filter-query/index.tsx';
import { useSearchKey } from '../services/issue-search/selectors.tsx';
import { useFlatListIssuesActions } from '../controllers/flat-list-issues/index.tsx';
import { useSetJql } from '../services/set-jql/index.tsx';
import { IFCEntryPointContextProvider } from '../common/ui/inline-field-config/context/index.tsx';
import { useIssueNavigatorFeatures } from '../controllers/features-context/index.tsx';
import { useHasListMigrationDataForProject } from '../services/list-nin-config-migration/useHasListMigrationDataForProject.tsx';
import { BulkOperationsWrapper } from './bulk-operations-wrapper/BulkOperationsWrapper.tsx';
import FullPageIssueApp from './full-page-issue-app/main.tsx';
import Header from './header/main.tsx';
import IssueResults from './issue-results/main.tsx';
import JQLBuilder, { type OverridableJqlBuilderProps } from './jql-builder/index.tsx';
import messages from './messages.tsx';
import RegisterDotShortcut from './register-dot-shortcut/index.tsx';
import IssueNavigatorSpotlight from './spotlight/index.tsx';
import TopBar from './top-bar/index.tsx';
import { SidebarCloserController } from './sidebar/sidebar-closer/index.tsx';
import { SidebarExperienceRegistryWithErrorBoundary } from './sidebar/experience-registry/index.tsx';
import { SpotlightExploreListClickListener } from './spotlight/explore-list-click-listener/index.tsx';
import { MigrateListConfigClickListener } from './spotlight/migrate-list-config-click-listener/MigrateListConfigClickListener.tsx';

const SidebarLayout = ({
	children,
	project,
}: PropsWithChildren<{ project: main_issueNavigator_SidebarLayout_project$key | null }>) => {
	const projectData = useFragment<main_issueNavigator_SidebarLayout_project$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_SidebarLayout_project on JiraProject
			@argumentDefinitions(
				isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
				isSavedViewConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
			) {
				...experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_project
					@arguments(
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isSavedViewConditionalFormattingEnabled: $isSavedViewConditionalFormattingEnabled
					)
			}
		`,
		project,
	);
	return (
		<BaseSidebarLayout
			defaultRatio={0.3}
			minWidth={360}
			maxRatio={0.75}
			teamName="empanada"
			content={<SidebarExperienceRegistryWithErrorBoundary project={projectData ?? null} />}
		>
			{children}
			<SidebarCloserController />
		</BaseSidebarLayout>
	);
};

const staticEntrypointParams = {
	cloudId: undefined,
	projectKey: undefined,
};

type Props = {
	issueResults: IssueResultsFragment | null;
	groupResults: GroupResultsFragment | null;
	CustomHeader?: ComponentType<CustomHeaderProps>;
	ActionMenu?: ComponentType<CustomHeaderProps>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	HeaderSkeletonImage: ComponentType<Record<any, any>>;
	defaultJql: string;
	filter:
		| (HeaderFilterFragmentType &
				TopBarFilterFragment &
				JqlBuilderFilterFragmentType &
				ListViewFilterFragmentType)
		| null;
	project: ProjectFragment | null;
	userPreferences: UserPreferencesFragmentKey | null;
	/**
	 * Callback function called when internal changes are made to NINs state which generates new JQL and groupBy.
	 * This allows us to update both values in the URL at the same time, allowing the data to be passed back down the tree.
	 * This is introduced strictly for jira_list_nin_merge changeboarding, and can be removed on cleanup.
	 */
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
	onPageDataLoad?: (selectedView: IssueNavigatorViewId) => void;
	onChangeFilter?: (filterId?: string) => void;
	onRefinement?: () => void;
	onChangeIssue: (
		issueKey: IssueKey,
		isSelectedByUserInteraction: boolean,
		viewActivity?: boolean,
	) => void;
	onChangeColumnConfiguration?: () => void;
	onChangeJql?: (jql: string, clearFilter?: boolean) => void;
	onIssueFlatListUpdated?: (
		newFlatKeyList: string[],
		newFlatIdList: string[],
		newFlatChildIdList: string[],
	) => void;
	jqlBuilderProps?: OverridableJqlBuilderProps;
	jqlBuilderWithAiKey: JqlBuilderWithAiKey | null;
	issueTableProps?: OverridableIssueTableProps;
	isGridNavigationEnabled?: boolean;
	isIssueHierarchySupportEnabled?: boolean;
	isIssueRankingSupportEnabled?: boolean;
	isMergedColumnSupportEnabled?: boolean;
	isFrozenColumnSupportEnabled?: boolean;
	isInlineFieldConfigEnabled?: boolean;
	isInBetweenColumnPickerEnabled?: boolean;
	jiraView: JiraViewFragment | null;
	/** @deprecated behind jira_nin_last_used_jql_tech_changes */
	initialQueryAsJql?: string;
	isInitialized: boolean;
	// Clean up when cleaning jira_list_nin_merge FG
	isPostOfficePlacementEnabled?: boolean;
};

export const IssueNavigatorUI = ({
	CustomHeader,
	ActionMenu,
	HeaderSkeletonImage,
	filter,
	onChangeJqlAndGroupBy,
	onPageDataLoad,
	onChangeColumnConfiguration,
	onChangeJql,
	onChangeIssue,
	onChangeFilter,
	defaultJql,
	onRefinement,
	onIssueFlatListUpdated,
	issueResults,
	groupResults,
	project,
	userPreferences,
	jqlBuilderProps,
	jqlBuilderWithAiKey,
	issueTableProps,
	isGridNavigationEnabled,
	isIssueHierarchySupportEnabled,
	isIssueRankingSupportEnabled,
	isMergedColumnSupportEnabled,
	isFrozenColumnSupportEnabled,
	isInlineFieldConfigEnabled,
	isInBetweenColumnPickerEnabled,
	jiraView,
	initialQueryAsJql,
	isInitialized,
	isPostOfficePlacementEnabled,
}: Props) => {
	markOnce(marks.ISSUE_NAVIGATOR_UI_START);
	useLayoutEffect(() => {
		markOnce(marks.ISSUE_NAVIGATOR_UI_END);
	}, []);
	const { formatMessage } = useIntl();

	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const setJql = fg('saved_list_view_technical_changes') ? useSetJql() : noop;
	const [caughtIssueSearchError, setCaughtIssueSearchError] = useState<Error>();

	const { filterId } = useActiveJql();
	const resolvedJql = useResolvedJql();
	const { filterQueryIsFetching } = useFilterQuery();

	const searchKey = useSearchKey();
	const previousData = usePrevious(searchKey);
	const hasSearchChanged = Boolean(searchKey && searchKey !== previousData);

	const [{ view: selectedView }] = useSelectedViewState();

	const issueResultsData = useFragment<IssueResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigatorUI_issueResults on JiraIssueConnection
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isBulkOpsSelectAllEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/bulk-ops-select-all.relayprovider"
				}
			) {
				...topBar_issueNavigator_issueResults
				...main_issueNavigator_Header_issueResults
				...jqlBuilder_issueNavigator_JQLBuilderWrapper_issueResults
				...main_issueNavigator_IssueResults_issueResults
					@arguments(
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
					)
				...main_issueNavigator_FullPageIssueApp_issueResults
				issueSearchError {
					__typename
					... on JiraServerError {
						message
					}
				}
				totalIssueSearchResultCount
				isCappingIssueSearchResult
				...BulkOperationsWrapper
				pageInfo @include(if: $isBulkOpsSelectAllEnabled) {
					hasPreviousPage
				}
			}
		`,
		issueResults,
	);

	const groupResultsData = useFragment<GroupResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigatorUI_groupResults on JiraSpreadsheetGroupConnection
			@argumentDefinitions(projectKeys: { type: "[String!]" }, projectKey: { type: "String" }) {
				...main_issueNavigator_IssueResults_groupResults
					@arguments(projectKeys: $projectKeys, projectKey: $projectKey)
				...jqlBuilder_issueNavigator_JQLBuilderWrapper_groupResults
				errors {
					extensions {
						statusCode
					}
					message
				}
			}
		`,
		groupResults,
	);

	const jiraViewData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigatorUI_jiraView on JiraView
			@argumentDefinitions(
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
				isFrozenColumnsEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-frozen-columns-enabled.relayprovider.tsx"
				}
				isBulkOpsSelectAllBeyondExperimentEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/bulk-ops-select-all.relayprovider"
				}
			) {
				__typename
				error {
					extensions {
						statusCode
					}
					message
				}
				...main_issueNavigator_Header_jiraViewResult
				...main_issueNavigator_IssueResults_jiraView
				...topBar_issueNavigator_jiraViewResult
				...MigrateListConfigClickListener_JiraView
				... on JiraIssueSearchViewMetadata {
					...main_issueNavigator_FullPageIssueApp_view
					savedViewId @include(if: $isSavedListViewTechnicalChangesEnabled)
					fieldSets(
						first: $amountOfColumns
						filter: { fieldSetSelectedState: SELECTED }
						scope: $fieldSetsScope
					) {
						edges {
							node {
								fieldSetPreferences {
									isFrozen @include(if: $isFrozenColumnsEnabled)
								}
							}
						}
					}
				}
				... on JiraSpreadsheetView {
					...BulkOperationsWrapper_ViewSettings
						@include(if: $isBulkOpsSelectAllBeyondExperimentEnabled)
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						isGroupingEnabled
						canEnableGrouping
					}
				}
			}
		`,
		jiraView,
	);

	const hasStickyColumn = fg('issue_table_frozen_columns')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(
				() =>
					jiraViewData?.fieldSets?.edges?.some(
						(edge) => edge?.node?.fieldSetPreferences?.isFrozen === true,
					) ?? false,
				[jiraViewData?.fieldSets?.edges],
			)
		: false;

	const handleChangeJql = useCallback(
		(jql: string, clearFilter?: boolean) => {
			onChangeJql?.(jql, clearFilter);

			if (fg('saved_list_view_technical_changes')) {
				const savedViewId = jiraViewData?.savedViewId;

				if (savedViewId == null) {
					throw new Error('Missing saved view ID');
				}

				setJql({
					jql,
					viewARI: savedViewId,
				});
			}
		},
		[jiraViewData?.savedViewId, onChangeJql, setJql],
	);

	const onChange = useCallback(
		(jql: string) => {
			onRefinement && onRefinement();
			handleChangeJql(jql, !filterQueryIsFetching && filter === null && isFilterId(filterId));
		},
		[filter, filterId, filterQueryIsFetching, handleChangeJql, onRefinement],
	);

	const handleReset = useCallback(
		(isResetToFilter = false) => {
			if (isResetToFilter) {
				onChangeFilter && onChangeFilter();
			} else {
				handleChangeJql(defaultJql, true);
			}
		},
		[onChangeFilter, handleChangeJql, defaultJql],
	);

	const viewResultError = useMemo(() => {
		if (!jiraViewData) return undefined;

		const message = jiraViewData.error?.message ?? '';
		const statusCode = jiraViewData.error?.extensions?.[0].statusCode;

		if (typeof statusCode !== 'number') {
			return;
		}

		return new FetchError(statusCode, message);
	}, [jiraViewData]);

	const userPreferencesData = useFragment<UserPreferencesFragmentKey>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigatorUI_userPreferences on JiraUserPreferences {
				...spotlight_issueNavigator_IssueNavigatorSpotlight
			}
		`,
		userPreferences,
	);

	const jqlBuilderWithAiData = useFragment<JqlBuilderWithAiKey>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigatorUI_jqlBuilderWithAiKey on JiraQuery {
				...jqlBuilder_issueNavigator_JQLBuilderWrapper_jqlBuilderWithAiKey
				...main_issueNavigator_IssueResults_rankField
			}
		`,
		jqlBuilderWithAiKey,
	);

	const projectData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigatorUI_project on JiraProject
			@argumentDefinitions(
				isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
				isSavedViewConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
				isFiltersMenuEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-nin-saved-filters-enabled.relayprovider"
				}
			) {
				...main_issueNavigator_IssueResults_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
				...main_issueNavigator_SidebarLayout_project
					@arguments(
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isSavedViewConditionalFormattingEnabled: $isSavedViewConditionalFormattingEnabled
					)
				...topBar_issueNavigator_projectResult @include(if: $isFiltersMenuEnabled)
				...MigrateListConfigClickListener_JiraProject
				projectId
				projectType
				conditionalFormattingRules(first: 20) @include(if: $isConditionalFormattingEnabled) {
					edges {
						__typename
					}
				}
			}
		`,
		project,
	);

	const isConditionalFormattingEnabled =
		fg('jira_list_conditional_formatting') &&
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useIssueNavigatorFeatures().isConditionalFormattingEnabled;

	const hasFormattingRules = (projectData?.conditionalFormattingRules?.edges?.length ?? 0) > 0;

	const isGrouping = useMemo(
		() =>
			issueTableProps?.isGroupingSupported &&
			jiraViewData?.viewSettings?.isGroupingEnabled &&
			jiraViewData?.viewSettings?.canEnableGrouping,
		[
			issueTableProps?.isGroupingSupported,
			jiraViewData?.viewSettings?.canEnableGrouping,
			jiraViewData?.viewSettings?.isGroupingEnabled,
		],
	);

	const hasPreviousPage = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(
				() => issueResultsData?.pageInfo?.hasPreviousPage,
				[issueResultsData?.pageInfo?.hasPreviousPage],
			)
		: undefined;

	const screenEventAttributes = useMemo(() => {
		if (
			!fg('jira_list_conditional_formatting') &&
			!fg('issue_table_frozen_columns') &&
			!fg('group_by_any_fields_technical')
		) {
			return undefined;
		}
		return {
			...(fg('jira_list_conditional_formatting') && {
				isConditionalFormattingEnabled,
				hasFormattingRules,
			}),
			...(fg('issue_table_frozen_columns') && {
				isFrozenColumnSupportEnabled,
				hasStickyColumn,
			}),
			...(fg('group_by_any_fields_technical') && {
				isGrouping,
			}),
		};
	}, [
		hasFormattingRules,
		hasStickyColumn,
		isConditionalFormattingEnabled,
		isFrozenColumnSupportEnabled,
		isGrouping,
	]);

	const shouldAnnounceInfo = useMemo(
		() =>
			hasSearchChanged &&
			(isGrouping
				? groupResultsData !== null
				: issueResultsData !== null &&
					typeof issueResultsData?.totalIssueSearchResultCount === 'number'),
		[hasSearchChanged, isGrouping, groupResultsData, issueResultsData],
	);

	const [isAiEnabled, setIsAiEnabled] = useState(false);
	const onToggleAi = useCallback((isEnabled: boolean) => {
		setIsAiEnabled(isEnabled);
	}, []);

	const serverError = useMemo(() => {
		if (issueResultsData?.issueSearchError?.__typename === 'JiraServerError') {
			return new Error(issueResultsData.issueSearchError.message ?? 'Unknown issue search error');
		}

		if (groupResultsData?.errors?.length) {
			const firstError = groupResultsData.errors.find(
				({ extensions }) =>
					!extensions?.some(
						({ statusCode }) =>
							typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500,
					),
			);

			return firstError
				? new Error(firstError.message ?? 'Unknown group results error')
				: undefined;
		}
	}, [groupResultsData?.errors, issueResultsData?.issueSearchError]);
	const hasServerError = fg('empanada_nin_more_issue_search_fail_attributes')
		? !!serverError
		: issueResultsData?.issueSearchError?.__typename === 'JiraServerError' ||
			(groupResultsData?.errors ?? []).some(
				({ extensions }) =>
					!extensions?.some(
						({ statusCode }) =>
							typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500,
					),
			);

	const hasViewNotFoundError = !!viewResultError && viewResultError.statusCode === 404;

	const noFilterReason: string | undefined = useMemo(() => {
		if (!filterQueryIsFetching && filter === null && isFilterId(filterId)) {
			return 'Filter query mismatch';
		}
		if (hasViewNotFoundError) {
			return 'Filter not found in view result';
		}
		return undefined;
	}, [filter, filterId, filterQueryIsFetching, hasViewNotFoundError]);

	const IssueNavigatorTopBar = useMemo(() => {
		const jqlBuilder = (
			<JQLBuilder
				onChangeJql={onChange}
				defaultJql={defaultJql}
				filter={filter}
				onReset={handleReset}
				onFilterSave={onChangeFilter}
				groupResults={groupResultsData ?? null}
				issueResults={issueResultsData ?? null}
				onToggleAi={onToggleAi}
				jqlBuilderWithAiKey={jqlBuilderWithAiData ?? null}
				jqlBuilderProps={jqlBuilderProps}
				initialQueryAsJql={initialQueryAsJql}
				disableBackToFilterButton={!!projectData}
			/>
		);

		return (
			<>
				{fg('nin_blindspot_fixes_fy26') && (
					<UFOLoadHold name="issue-navigator-top-bar" hold={!ActionMenu && !CustomHeader} />
				)}
				{ActionMenu && (
					<TopBar
						ActionMenu={ActionMenu}
						isGroupingSupported={issueTableProps?.isGroupingSupported}
						filter={filter}
						issueResults={issueResultsData ?? null}
						HeaderSkeletonImage={HeaderSkeletonImage}
						jqlBuilder={jqlBuilder}
						jiraViewResult={jiraViewData ?? null}
						projectResult={fg('nin-saved-filters') ? (projectData ?? null) : null}
						onChangeFilter={onChangeFilter}
						onChangeJql={handleChangeJql}
						isGridNavigationEnabled={isGridNavigationEnabled}
						isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
						defaultJql={defaultJql}
					/>
				)}
				{CustomHeader && (
					<>
						<Header
							CustomHeader={CustomHeader}
							filter={filter}
							issueResults={issueResultsData ?? null}
							HeaderSkeletonImage={HeaderSkeletonImage}
							jiraViewResult={jiraViewData ?? null}
							isGroupingSupported={issueTableProps?.isGroupingSupported}
							onChangeFilter={onChangeFilter}
						/>
						{jqlBuilder}
					</>
				)}
			</>
		);
	}, [
		onChange,
		defaultJql,
		filter,
		handleReset,
		onChangeFilter,
		groupResultsData,
		issueResultsData,
		onToggleAi,
		jqlBuilderWithAiData,
		jqlBuilderProps,
		initialQueryAsJql,
		projectData,
		ActionMenu,
		CustomHeader,
		issueTableProps?.isGroupingSupported,
		HeaderSkeletonImage,
		jiraViewData,
		handleChangeJql,
		isGridNavigationEnabled,
		isInlineFieldConfigEnabled,
	]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncBulkOpsCoreEntryPoint,
		staticEntrypointParams,
	);

	let setFlatListIssueIds = noop;
	let setFlatListChildIssueIds = noop;
	if (fg('jsc_m2_hierarchy_fe_changes')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ setFlatListIssueIds } = useFlatListIssuesActions());
	}
	if (expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ setFlatListChildIssueIds } = useFlatListIssuesActions());
	}

	const hasListMigrationData = fg('jira_list_nin_merge')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useHasListMigrationDataForProject(projectData?.projectId)
		: false;

	const handleIssueFlatListUpdated = useCallback(
		(newFlatKeyList: string[], newFlatIdList: string[], newFlatChildIdList: string[]) => {
			setFlatListIssueIds(newFlatIdList);
			if (expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) {
				setFlatListChildIssueIds(newFlatChildIdList);
			}
			if (onIssueFlatListUpdated) {
				onIssueFlatListUpdated(newFlatKeyList, newFlatIdList, newFlatChildIdList);
			}
		},
		[setFlatListIssueIds, setFlatListChildIssueIds, onIssueFlatListUpdated],
	);

	const {
		isDeleteIssueModalOpen,
		isDeletingIssue,
		issueId,
		issueKey,
		didIssueDeleteFail,
		workType,
		isIssueArchivalEnabled,
		onTriggerDeleteRequest,
	} = useDeleteIssueModalStates();

	const { closeDeleteIssueModal } = useDeleteIssueModalActions();

	const isTriggeredFromNINView = useMemo(() => {
		return { isIssueArchivalEnabled, isTriggeredFromNINView: true };
	}, [isIssueArchivalEnabled]);

	let IssueNavigatorContent = useMemo(() => {
		const getAnnouncerMessage = () => {
			if (isGrouping) {
				return formatMessage(messages.screenReaderGroupResultInfo);
			}

			let message;
			if (issueResultsData?.isCappingIssueSearchResult) {
				message = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.screenReaderSearchResultMoreInfoIssueTermRefresh
					: messages.screenReaderSearchResultMoreInfo;
			} else {
				message = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.screenReaderSearchResultInfoIssueTermRefresh
					: messages.screenReaderSearchResultInfo;
			}

			return formatMessage(message, {
				total: issueResultsData?.totalIssueSearchResultCount,
			});
		};

		const HierarchyOnboardingProviderComponent = fg('jsc_m2_hierarchy_fe_changes')
			? HierarchyOnboardingProvider
			: Fragment;

		const HideDoneOnboardingProviderComponent = fg('jsc_list_hide_done_items')
			? HideDoneOnboardingProvider
			: Fragment;

		const InlineFieldConfigProviderComponent = isInlineFieldConfigEnabled
			? IFCEntryPointContextProvider
			: Fragment;

		return (
			<IssueTableSelectedIssuesContainer
				onIssueSelected={entryPointActions.load}
				{...(expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false) && {
					shouldForceManualSelectAll: isGrouping || hasPreviousPage,
				})}
			>
				<IssueFieldStatisticsEntrypointProvider jql={resolvedJql} view={selectedView}>
					<SidebarLayout project={projectData ?? null}>
						<BulkOperationsWrapper
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore - error TS2322: - type mismatch
							bulkOpsCoreEntryPointReference={entryPointReferenceSubject}
							fragmentRef={issueResultsData ?? null}
							{...(expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false) && {
								jiraViewFragment: jiraViewData ?? null,
							})}
						>
							<HierarchyOnboardingProviderComponent>
								<InlineFieldConfigProviderComponent>
									<IssueNavigatorContentContainer
										selectedView={selectedView}
										data-vc={`issue-navigator-content${__SERVER__ ? '-ssr' : ''}`}
										{...(__SERVER__ && {
											'data-ssr-placeholder': 'issue-navigator-content-placeholder',
										})}
										{...(!__SERVER__ && {
											'data-ssr-placeholder-replace': 'issue-navigator-content-placeholder',
										})}
									>
										<HideDoneOnboardingProviderComponent>
											{IssueNavigatorTopBar}
										</HideDoneOnboardingProviderComponent>

										{/* We render the JQL Fixer panel here in small screens to take the full available width */}
										<JQLPanelRenderSlotForWorkItemList />

										{(!filterQueryIsFetching && filter === null && isFilterId(filterId)) ||
										hasViewNotFoundError ? (
											<NoFilter reason={noFilterReason} />
										) : (
											<>
												<AnnouncerV2
													message={getAnnouncerMessage()}
													shouldAnnounce={shouldAnnounceInfo}
													testId="issue-navigator.ui.search-announcer"
												/>
												<IssueResults
													hasServerError={hasServerError}
													onChangeColumnConfiguration={onChangeColumnConfiguration}
													onChangeJql={handleChangeJql}
													onCaughtIssueSearchError={setCaughtIssueSearchError}
													issueResults={issueResultsData ?? null}
													jiraView={jiraViewData ?? null}
													project={projectData ?? null}
													rankField={jqlBuilderWithAiData ?? null}
													groupResults={groupResultsData ?? null}
													issueTableProps={issueTableProps}
													isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
													isIssueRankingSupportEnabled={isIssueRankingSupportEnabled}
													isMergedColumnSupportEnabled={isMergedColumnSupportEnabled}
													isFrozenColumnSupportEnabled={isFrozenColumnSupportEnabled}
													isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
													isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
													onIssueFlatListUpdated={handleIssueFlatListUpdated}
													filter={filter}
												/>
												{isPostOfficePlacementEnabled &&
													// Disabled in SSR
													!__SERVER__ &&
													fg('jira_list_nin_merge') && (
														<IssueNavigatorPlacement
															isDetailedView={selectedView === 'detail'}
															hasListPreference={hasListMigrationData}
														/>
													)}
											</>
										)}
										<IssueNavigatorSpotlight
											hasIssueResults={!!issueResultsData?.totalIssueSearchResultCount}
											userPreferences={userPreferencesData ?? null}
											isAiEnabled={isAiEnabled}
										/>
									</IssueNavigatorContentContainer>
								</InlineFieldConfigProviderComponent>
							</HierarchyOnboardingProviderComponent>
						</BulkOperationsWrapper>
					</SidebarLayout>
				</IssueFieldStatisticsEntrypointProvider>
			</IssueTableSelectedIssuesContainer>
		);
	}, [
		isInlineFieldConfigEnabled,
		entryPointActions.load,
		isGrouping,
		hasPreviousPage,
		resolvedJql,
		selectedView,
		projectData,
		entryPointReferenceSubject,
		issueResultsData,
		jiraViewData,
		IssueNavigatorTopBar,
		filterQueryIsFetching,
		filter,
		filterId,
		hasViewNotFoundError,
		noFilterReason,
		shouldAnnounceInfo,
		hasServerError,
		onChangeColumnConfiguration,
		handleChangeJql,
		jqlBuilderWithAiData,
		groupResultsData,
		issueTableProps,
		isIssueHierarchySupportEnabled,
		isIssueRankingSupportEnabled,
		isMergedColumnSupportEnabled,
		isFrozenColumnSupportEnabled,
		isInBetweenColumnPickerEnabled,
		handleIssueFlatListUpdated,
		isPostOfficePlacementEnabled,
		hasListMigrationData,
		userPreferencesData,
		isAiEnabled,
		formatMessage,
	]);

	const isFullPageIssueAppMode = useIsFullPageIssueAppMode();

	if (isFullPageIssueAppMode) {
		const view = jiraViewData && isJiraIssueSearchViewMetadata(jiraViewData) ? jiraViewData : null;

		IssueNavigatorContent = (
			<FullPageIssueApp
				onChangeIssue={onChangeIssue}
				issueResults={issueResultsData ?? null}
				view={view}
				{...(fg('add-attribute-required-for-colorful-labels-expe')
					? { projectType: projectData?.projectType }
					: {})}
			/>
		);
	}

	useGlobalScopeOptInFlag();

	const hasIssueResultsData = !!issueResultsData;
	const hasGroupResultsData = !!groupResultsData;
	const hasJiraViewData = !!jiraViewData;
	const hasIssueSearchError = !!caughtIssueSearchError;
	const hasViewResultError = !!viewResultError;

	const hasSearchError = hasServerError || hasIssueSearchError;
	const hasSearchResults = hasIssueResultsData || hasGroupResultsData;
	const isFailedIssueSearch =
		!hasSearchResults ||
		hasSearchError ||
		!hasJiraViewData ||
		(!!viewResultError && viewResultError.statusCode !== 404);

	const isBifrost = !!ssr.getFeatureFlags()?.ssrViaBifrost;

	return useMemo(
		() => (
			<>
				{IssueNavigatorContent}
				{fg('jira_list_nin_merge') && <SpotlightExploreListClickListener />}
				{projectData && jiraViewData && fg('jira_list_nin_merge') && (
					<MigrateListConfigClickListener
						onChangeJqlAndGroupBy={onChangeJqlAndGroupBy}
						project={projectData}
						view={jiraViewData}
					/>
				)}
				<RegisterDotShortcut />
				{isInitialized && <FireScreenAnalytics attributes={screenEventAttributes} />}
				{isFailedIssueSearch ? (
					<IssueSearchFail
						location={`${PACKAGE_NAME}.ui`}
						error={
							caughtIssueSearchError ||
							viewResultError ||
							(fg('empanada_nin_more_issue_search_fail_attributes')
								? serverError || new Error('There was an unknown error searching for issues')
								: new Error('There was an error searching for issues'))
						}
						onPageDataLoad={onPageDataLoad}
						attributes={{
							isAiEnabled,
							...(isGrouping ? { experienceId: GROUPED_LIST_EXPERIENCE_ID } : {}),
							isBifrost,
							...(fg('empanada_nin_more_issue_search_fail_attributes')
								? {
										hasIssueSearchError,
										hasViewResultError,
										hasServerError,
										hasIssueResultsData,
										hasGroupResultsData,
										hasJiraViewData,
									}
								: {}),
						}}
					/>
				) : (
					<IssueSearchSuccess
						onPageDataLoad={onPageDataLoad}
						attributes={{
							isAiEnabled,
							...(isGrouping ? { experienceId: GROUPED_LIST_EXPERIENCE_ID } : {}),
							isBifrost,
						}}
					/>
				)}
				{fg('issue_deletion_two_step_process_free_standard') && (
					<DeleteIssueView
						issueId={issueId}
						isDeleteIssueModalOpen={isDeleteIssueModalOpen}
						isDeletingIssue={isDeletingIssue}
						issueKey={issueKey}
						workTypeKey={workType}
						isSimplifiedProject={false}
						shouldShowChildSpecificCopy
						didIssueDeleteFail={didIssueDeleteFail}
						triggerDeleteRequest={onTriggerDeleteRequest}
						triggerCloseDeleteIssueModal={closeDeleteIssueModal}
						{...(fg('additional-check-for-trigger-from-nin-view')
							? { isTriggeredFromNINView }
							: { isIssueArchivalEnabled })}
					/>
				)}
			</>
		),
		[
			IssueNavigatorContent,
			isInitialized,
			screenEventAttributes,
			isFailedIssueSearch,
			caughtIssueSearchError,
			viewResultError,
			serverError,
			onChangeJqlAndGroupBy,
			onPageDataLoad,
			projectData,
			isAiEnabled,
			isGrouping,
			isBifrost,
			hasIssueSearchError,
			hasViewResultError,
			hasServerError,
			hasIssueResultsData,
			hasGroupResultsData,
			hasJiraViewData,
			jiraViewData,
			issueId,
			isDeleteIssueModalOpen,
			isDeletingIssue,
			issueKey,
			workType,
			didIssueDeleteFail,
			onTriggerDeleteRequest,
			closeDeleteIssueModal,
			isTriggeredFromNINView,
			isIssueArchivalEnabled,
		],
	);
};

// re-render regression detection
IssueNavigatorUI.whyDidYouRender = true;

export default memo<
	JSX.LibraryManagedAttributes<typeof IssueNavigatorUI, ComponentProps<typeof IssueNavigatorUI>>
>(IssueNavigatorUI);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueNavigatorContentContainer = styled.div<{ selectedView: IssueNavigatorViewId }>(
	{
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		marginTop: 0,
		marginRight: token('space.400'),
		marginBottom: 0,
		marginLeft: token('space.400'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ selectedView }) =>
		selectedView !== 'detail' && {
			paddingBottom: token('space.250'),
			boxSizing: 'border-box',
		},
);
